/**
 * Documentation System Configuration
 * 
 * This defines the structure and organization of user-facing documentation
 * stored in the RAG document system.
 */

// Documentation Document Type ID (to be created in admin settings)
export const DOCUMENTATION_TYPE_ID = 'DOCS'; // Placeholder - update after creating type

// Documentation sections
export const DOC_SECTIONS = {
  GETTING_STARTED: {
    id: 'getting-started',
    name: 'Getting Started',
    icon: '🚀',
    order: 1,
    description: 'Quick-start guides for students'
  },
  CORE_FEATURES: {
    id: 'core-features',
    name: 'Core Features',
    icon: '⚡',
    order: 2,
    description: 'Uploading, assignments, and calendar'
  },
  SCOUT_STUDY: {
    id: 'scout-study-tools',
    name: 'Scout AI & Study Tools',
    icon: '🤖',
    order: 3,
    description: 'Scout AI, flashcards, quizzes, and audio'
  },
  ACCOUNT_BILLING: {
    id: 'account-billing',
    name: 'Account & Billing',
    icon: '⚙️',
    order: 4,
    description: 'Manage your account and subscription'
  },
  HELP_TROUBLESHOOTING: {
    id: 'help-troubleshooting',
    name: 'Help & Troubleshooting',
    icon: '🔧',
    order: 5,
    description: 'Common issues and support'
  }
};

// Get sections as sorted array
export const getSortedSections = () => {
  return Object.values(DOC_SECTIONS).sort((a, b) => a.order - b.order);
};

// Centralized mapping of articles per section (used by viewer and sidebar)
export const ARTICLES_BY_SECTION = {
  'getting-started': [
    { id: 'account-setup', title: 'Account Setup Guide', file: 'getting-started/account-setup.md', description: 'Create and configure your DocuHounds account' },
    { id: 'creating-first-course', title: 'Creating Your First Course', file: 'getting-started/creating-first-course-student.md', description: 'Step-by-step guide to creating a course' },
    { id: 'navigating-dashboard', title: 'Navigating Your Dashboard', file: 'getting-started/navigating-dashboard-student.md', description: 'Learn to use the DocuHounds dashboard' }
  ],
  'core-features': [
    { id: 'adding-documents', title: 'Adding Course Documents', file: 'features/adding-documents-student.md', description: 'Upload and manage course materials' },
    { id: 'creating-assignments', title: 'Creating Assignments', file: 'features/creating-assignments-student.md', description: 'Create and manage student assignments (desktop web)' },
    { id: 'calendar-management', title: 'Calendar Management', file: 'calendar/calendar-management-student.md', description: 'Master your schedule and events' }
  ],
  'scout-study-tools': [
    { id: 'scout-overview', title: 'Scout: Your AI Study Assistant', file: 'ai-assistant/scout.md', description: 'Student guide to Scout (desktop web)' },
    { id: 'flashcards', title: 'Creating Flashcards', file: 'study-tools/flashcards-student.md', description: 'Master memorization with flashcards' },
    { id: 'creating-quizzes', title: 'Creating Quizzes', file: 'study-tools/creating-quizzes.md', description: 'Generate and take quizzes from your course materials' },
    { id: 'creating-podcasts', title: 'Creating Podcasts', file: 'study-tools/creating-podcasts.md', description: 'Generate audio podcasts from your documents' },
    { id: 'creating-audiobooks', title: 'Creating Audiobooks', file: 'study-tools/creating-audiobooks.md', description: 'Generate verbatim audiobooks from a single document' }
  ],
  'account-billing': [
    { id: 'account-settings', title: 'Managing Your Account', file: 'account/account-settings-student.md', description: 'Profile, security, and subscription settings' }
  ],
  'help-troubleshooting': [
    { id: 'common-issues', title: 'Troubleshooting Common Issues', file: 'troubleshooting/common-issues-student.md', description: 'Quick solutions to frequent problems' }
  ]
};

export const getArticlesForSection = (sectionId) => {
  return ARTICLES_BY_SECTION[sectionId] || [];
};

// Metadata field names for documentation
export const DOC_METADATA_FIELDS = {
  SECTION: 'Section',
  DIFFICULTY: 'Difficulty',
  KEYWORDS: 'Keywords',
  LAST_UPDATED: 'Last Updated',
  AUTHOR: 'Author',
  RELATED_ARTICLES: 'Related Articles'
};

// Difficulty levels
export const DOC_DIFFICULTY = {
  BEGINNER: 'Beginner',
  INTERMEDIATE: 'Intermediate',
  ADVANCED: 'Advanced'
};

// Sample documentation structure for initial setup
export const SAMPLE_DOCS = [
  {
    title: 'Creating Your First Course',
    section: 'getting-started',
    difficulty: 'Beginner',
    keywords: ['course', 'setup', 'wizard'],
    description: 'Learn how to create your first course in DocuHounds'
  },
  {
    title: 'Uploading Documents',
    section: 'getting-started',
    difficulty: 'Beginner',
    keywords: ['upload', 'files', 'documents', 'syllabus'],
    description: 'How to upload and organize your course materials'
  },
  {
    title: 'Using ScoutIQ Chat',
    section: 'ai-assistant',
    difficulty: 'Beginner',
    keywords: ['chat', 'ai', 'scout', 'questions'],
    description: 'Ask questions about your course materials with AI'
  },
  {
    title: 'Calendar Sync & Integration',
    section: 'calendar',
    difficulty: 'Intermediate',
    keywords: ['calendar', 'google', 'sync', 'events'],
    description: 'Connect your external calendars to DocuHounds'
  },
  {
    title: 'Creating Flashcard Decks',
    section: 'study-tools',
    difficulty: 'Beginner',
    keywords: ['flashcards', 'study', 'learning'],
    description: 'Generate and study with AI-powered flashcards'
  },
  {
    title: 'Audio Generation & Podcasts',
    section: 'study-tools',
    difficulty: 'Intermediate',
    keywords: ['audio', 'podcast', 'audiobook', 'tts'],
    description: 'Convert your documents to audio for learning on the go'
  },
  {
    title: 'Managing Subscriptions',
    section: 'account',
    difficulty: 'Beginner',
    keywords: ['billing', 'subscription', 'payment', 'plan'],
    description: 'Understand billing and subscription options'
  },
  {
    title: 'Troubleshooting Upload Errors',
    section: 'troubleshooting',
    difficulty: 'Intermediate',
    keywords: ['errors', 'upload', 'failed', 'fix'],
    description: 'Resolve common document upload issues'
  }
];
